///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { ActiveRestorableWindow } from './active-restorable-window';
import { GtthProjectUserSessionService } from '../logical-topology/engine/project-user-session.service';
import { ProjectWindowsEvents } from './project-windows-events.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';

@Injectable({
    providedIn: 'root'
})
export class ProjectWindowsPopupService {
    private dialogRef: MatDialogRef<any>;

    constructor(
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly historyService: GtthHistoryService,
        private readonly projectUserSessionService: GtthProjectUserSessionService
    ) {
        this.dialogRef = null;

        this.historyService.eventManager.getEventSubscription(ProjectWindowsEvents.CLOSE_MODAL_WINDOW).subscribe(() => {
            if (this.dialogRef) {
                this.dialogRef.close('cancel');
            }
        });
    }

    openWindow(component: ComponentType<any>, size: 'xl' | 'sm' | 'lg' = 'sm'): Promise<MatDialogRef<any>> {
        return new Promise<MatDialogRef<any>>((resolve, _reject) => {
            const isOpen = this.dialogRef !== null;
            if (isOpen) {
                resolve(this.dialogRef);
            } else {
                this.historyService.interactiveWindowsService.closeAll();
                setTimeout(() => {
                    this.dialogRef = this.projectModalRef(component, size);
                    resolve(this.dialogRef);
                }, 0);
            }
        });
    }

    private projectModalRef(component: ComponentType<any>, size: 'xl' | 'sm' | 'lg'): MatDialogRef<any> {
        let width: number;
        switch (size) {
            case 'sm':
                width = 800;
                break;
            case 'lg':
                width = 1000;
                break;
            case 'xl':
                width = 1200;
                break;
        }

        const dialogRef = this.dialog.open(component, {
            width: width + 'px',
            position: {
                top: '30px'
            },
            closeOnNavigation: true
        });

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([{ outlets: { popup: null } }], { replaceUrl: true });
            this.projectUserSessionService.clearActiveModelWindow();
            this.closeWindow();
        });

        if (dialogRef.componentInstance instanceof ActiveRestorableWindow) {
            const childOutlet = this.router.routerState.root.children.find((value) => value.outlet === 'popup');
            if (childOutlet) {
                this.projectUserSessionService.storeActiveModelWindow(childOutlet.routeConfig.path, dialogRef.componentInstance);
            }
        }

        return dialogRef;
    }

    public closeWindow() {
        if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = null;
        }
    }
}
